import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const PARAS = [
  "In His Image Movement Ministries, Inc. is a Christ-centered ministry founded by David Porter to strengthen men through faith, wellness, brotherhood, and personal growth.",
  "The ministry was established with the belief that men are better equipped to lead their families, serve their communities, and fulfill their God-given purpose when they are healthy in every area of life.",
  "Through supportive programs, devotionals, mentorship, and faith-based initiatives, In His Image Movement Ministries promotes the physical, emotional, and spiritual wellness of men.",
  "We believe no man was meant to walk alone. In brotherhood, men find honesty, accountability, and encouragement to keep growing in Christ.",
  "Under the leadership of founder David Porter, In His Image Movement Ministries is committed to helping men grow with purpose, live with integrity, and become a positive force within their families and communities.",
];

export default function About() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <div className="min-h-screen bg-bone pt-20 md:pt-14 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">Our Story</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 max-w-[18ch] leading-[1.02]">
            About In His Image Movement Ministries, Inc.
          </h1>
        </Reveal>

        {/* Story + inset photo */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mt-20">
          <div className="md:col-span-7 order-2 md:order-1">
            <div className="space-y-6">
              {PARAS.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <p className="font-sans text-base md:text-lg leading-relaxed text-charcoal/80">{p}</p>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 order-1 md:order-2">
            <Reveal>
              <div ref={imgRef} className="relative md:sticky md:top-32">
                <div className="relative overflow-hidden border border-charcoal/10">
                  <motion.img style={{ y }} src="/founder.jpg" alt="David Porter, Founder"
                    className="w-full h-[440px] md:h-[520px] object-cover object-top scale-110" />
                  <div className="absolute inset-0 bg-charcoal/5" />
                </div>
                <div className="border border-t-0 border-charcoal/10 p-6 bg-sand">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Founder</span>
                  <h3 className="font-serif text-2xl tracking-tight mt-1">David Porter</h3>
                  <p className="font-sans text-sm text-charcoal/60 mt-1">In His Image Movement Ministries, Inc.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Mission */}
        <section className="mt-28 md:mt-36">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 border-t border-charcoal/20 pt-16">
            <Reveal className="md:col-span-4">
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-black">01</span>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight mt-4">Our Mission</h2>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-8">
              <p className="font-serif text-2xl md:text-3xl font-light leading-snug text-charcoal">
                To empower men to achieve physical, emotional, and spiritual wellness through
                Christ-centered support, education, and personal development.
              </p>
              <p className="font-sans text-base md:text-lg leading-relaxed text-charcoal/75 mt-8">
                We walk alongside men in brotherhood — sharpening one another, praying together,
                and growing stronger in faith, family, and purpose.
              </p>
              <Link to="/register" data-testid="about-join-btn"
                className="group inline-flex items-center gap-3 mt-12 bg-charcoal text-bone px-8 py-4 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors duration-300">
                Join the Movement <ArrowRight size={16} strokeWidth={1.5} className="hover-arrow" />
              </Link>
            </Reveal>
          </div>
        </section>
      </div>
    </div>
  );
}
